//planning block
import React, { useState, useEffect, useMemo } from 'react';
import Layout from "../Component/Layout";
import Select from "react-select";
import { ToastContainer, toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { ORDER_API, MASTER_API } from "../apiConfig";
import { authHeaders } from "../utils/authHeaders";
import { FileText } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

// --- Shared dropdown styling (matches Home / Product Master / Sales Order look) ---
const dropdownOptionStyle = {
    control: (base) => ({
        ...base,
        minHeight: 36,
        height: 36,
        fontSize: '13px',
        borderColor: '#dee2e6',
        boxShadow: 'none',
        '&:hover': { borderColor: '#b3b3b3' }
    }),
    valueContainer: (base) => ({ ...base, padding: '0 8px', height: '36px', display: 'flex', alignItems: 'center' }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    dropdownIndicator: (base) => ({ ...base, padding: 4 }),
    menu: (base) => ({ ...base, fontSize: '13px', zIndex: 9999 }),
    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? '#1e293b' : state.isFocused ? '#f1f5f9' : 'white',
        color: state.isSelected ? 'white' : '#334155',
        cursor: 'pointer',
        fontSize: '13px'
    })
};


const USER_MANUAL_SCOPE = {
    moduleName: "Planning",
    process: "Planning",
    subProcess: "Planning Block"
};
const USER_MANUAL_LIST_API = `${MASTER_API}/user-manual/list`;

// Today's date as DD-MM-YYYY, used to show the release date instantly
// (optimistic UI) before the next list refresh brings the server value.
const todayDDMMYYYY = () => {
    const d = new Date();
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${dd}-${mm}-${d.getFullYear()}`;
};

function PlanningBlock() {
    const navigate = useNavigate();
    const location = useLocation();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    // Tracks which row's dropdown is mid-save, so we can disable just that
    // one row instead of freezing the whole table on every change.
    const [savingId, setSavingId] = useState(null);

    const [showManual, setShowManual] = useState(false);
    const [manualExists, setManualExists] = useState(null);
    const [manualFile, setManualFile] = useState(null);

    // ── Bulk selection state ──
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkReleasing, setBulkReleasing] = useState(false);

    // ── Filters (react-select, client-side against the already-loaded list) ──
    const [soFilter, setSoFilter] = useState(null);
    const [codeFilter, setCodeFilter] = useState(null);
    const [descFilter, setDescFilter] = useState(null);
    const [blockFilter, setBlockFilter] = useState('Blocked'); // 'Blocked' | 'Released'

    // Unique option lists, built from whatever's currently loaded.
    const soOptions = useMemo(() => (
        [...new Set(rows.map(r => r.so_id).filter(Boolean))]
            .map(v => ({ value: v, label: v }))
    ), [rows]);

    const codeOptions = useMemo(() => (
        [...new Set(rows.map(r => r.product_code).filter(Boolean))]
            .map(v => ({ value: v, label: v }))
    ), [rows]);

    const descOptions = useMemo(() => (
        [...new Set(rows.map(r => r.description).filter(Boolean))]
            .map(v => ({ value: v, label: v }))
    ), [rows]);

    const filteredRows = useMemo(() => {
        return rows.filter(row => {
            if (soFilter && row.so_id !== soFilter.value) return false;
            if (codeFilter && row.product_code !== codeFilter.value) return false;
            if (descFilter && row.description !== descFilter.value) return false;
            if (row.planning_block !== blockFilter) return false;
            return true;
        });
    }, [rows, soFilter, codeFilter, descFilter, blockFilter]);

    // Counts for the pill badges — reflect the SO/Code/Description filters
    // but not the Planning Block toggle itself, so each pill shows how many
    // rows would appear if it were selected.
    const { blockedCount, releasedCount } = useMemo(() => {
        const base = rows.filter(row => {
            if (soFilter && row.so_id !== soFilter.value) return false;
            if (codeFilter && row.product_code !== codeFilter.value) return false;
            if (descFilter && row.description !== descFilter.value) return false;
            return true;
        });
        return {
            blockedCount: base.filter(r => r.planning_block === 'Blocked').length,
            releasedCount: base.filter(r => r.planning_block === 'Released').length,
        };
    }, [rows, soFilter, codeFilter, descFilter]);

    const fetchList = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${ORDER_API}/planning-block/list`, {
                headers: authHeaders()
            });
            const data = await res.json();
            setRows(data.data || []);
        } catch (err) {
            console.error("fetchList Error:", err);
            toast.error("Failed to load Planning Block list");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
    }, []);

    // Checkbox helpers
    const toggleRow = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const allVisibleSelected = filteredRows.length > 0 && filteredRows.filter(r => r.planning_block !== 'Released').every(r => selectedIds.has(r.id)) && filteredRows.some(r => r.planning_block !== 'Released');
    const someVisibleSelected = filteredRows.some(r => selectedIds.has(r.id));

    const toggleSelectAll = () => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            // Only Blocked rows are selectable — already-Released rows can't be re-released.
            const selectableRows = filteredRows.filter(r => r.planning_block !== 'Released');
            if (allVisibleSelected) {
                selectableRows.forEach(r => next.delete(r.id));
            } else {
                selectableRows.forEach(r => next.add(r.id));
            }
            return next;
        });
    };

    // Bulk release — applies to every currently selected row that isn't already Released.
    const handleBulkRelease = async () => {
        const selectedRows = rows.filter(r => selectedIds.has(r.id) && r.planning_block !== 'Released');
        if (!selectedRows.length) return;

        const prevValues = new Map(selectedRows.map(r => [r.id, r.planning_block]));

        // Optimistic UI update
        setRows(prev => prev.map(r => selectedIds.has(r.id) ? { ...r, planning_block: 'Released', planning_released_date: todayDDMMYYYY() } : r));
        setBulkReleasing(true);

        try {
            const res = await fetch(`${ORDER_API}/planning-block/bulk`, {
                method: "PUT",
                headers: { "Content-Type": "application/json", ...authHeaders() },
                body: JSON.stringify({
                    planning_block: 'Released',
                    items: selectedRows.map(r => ({ id: r.id, so_id: r.so_id }))
                })
            });
            const data = await res.json();
            if (!data.success) throw new Error(data.message || "Bulk update failed");
            toast.success(`Released ${selectedRows.length} line item(s)`);
            setSelectedIds(new Set());
        } catch (err) {
            console.error("bulkUpdatePlanningBlock Error:", err);
            toast.error("Failed to release selected items");
            // Roll back
            setRows(prev => prev.map(r => prevValues.has(r.id) ? { ...r, planning_block: prevValues.get(r.id), planning_released_date: null } : r));
        } finally {
            setBulkReleasing(false);
        }
    };

    // Instant update — fires as soon as the dropdown changes, no Save button.
    const handleChange = async (row, value) => {
        const prevValue = row.planning_block;

        // Optimistic UI update so the dropdown feels instant.
        setRows(prev => prev.map(r => r.id === row.id ? { ...r, planning_block: value, planning_released_date: value === 'Released' ? todayDDMMYYYY() : null } : r));
        setSavingId(row.id);

        try {
            const res = await fetch(`${ORDER_API}/planning-block/${row.so_id}/${row.id}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json", ...authHeaders() },
                body: JSON.stringify({ planning_block: value })
            });
            const data = await res.json();
            if (!data.success) throw new Error(data.message || "Update failed");
            toast.success(`${row.so_id} — Planning Block set to ${value}`);
        } catch (err) {
            console.error("updatePlanningBlock Error:", err);
            toast.error("Failed to update Planning Block");
            // Roll back on failure.
            setRows(prev => prev.map(r => r.id === row.id ? { ...r, planning_block: prevValue, planning_released_date: row.planning_released_date || null } : r));
        } finally {
            setSavingId(null);
        }
    };

    return (
        <Layout>
            <style>{`
                .pb-tab-toggle { display: inline-flex; background: #f1f5f9; border-radius: 8px; padding: 3px; gap: 2px; }
                .pb-tab-toggle button { border: none; background: transparent; border-radius: 6px; padding: 5px 14px; font-size: 13px; font-weight: 600; color: #64748b; cursor: pointer; transition: all 0.15s ease; display: flex; align-items: center; gap: 6px; }
                .pb-tab-toggle button.pb-active-tab { background: #fff; color: #1e293b; box-shadow: 0 1px 3px rgba(0,0,0,0.12); }
                .pb-badge { display: inline-flex; align-items: center; justify-content: center; min-width: 18px; height: 18px; border-radius: 9px; font-size: 11px; font-weight: 700; padding: 0 5px; line-height: 1; }
                .pb-badge-blocked { background: #fee2e2; color: #b91c1c; }
                .pb-badge-released { background: #dcfce7; color: #15803d; }
            `}</style>
            <div style={styles.pageTitleBar}>
                <div>
                    <div style={styles.pageTitle}>Planning Block</div>
                    <div style={styles.pageSubtitle}>Release or block Sales Order line items for production planning</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <button
                        type="button"
                        style={{
                            ...styles.releaseBtn,
                            ...(selectedIds.size === 0 || bulkReleasing ? styles.releaseBtnDisabled : {})
                        }}
                        disabled={selectedIds.size === 0 || bulkReleasing}
                        onClick={handleBulkRelease}
                    >
                        {bulkReleasing ? "Releasing..." : `Release${selectedIds.size ? ` (${selectedIds.size})` : ""}`}
                    </button>
                    <button
                        onClick={() => {
                            setManualExists(null);
                            setManualFile(null);
                            setShowManual(true);
                            fetch(USER_MANUAL_LIST_API, { headers: authHeaders() })
                                .then((res) => res.json())
                                .then((data) => {
                                    const norm = (v) => String(v || "").trim().toLowerCase();
                                    const match = (data?.data || []).find((m) =>
                                        norm(m.module_name) === norm(USER_MANUAL_SCOPE.moduleName) &&
                                        norm(m.process_name) === norm(USER_MANUAL_SCOPE.process) &&
                                        norm(m.sub_process_name) === norm(USER_MANUAL_SCOPE.subProcess)
                                    );
                                    if (match && match.drive_link) {
                                        setManualFile(match);
                                        setManualExists(true);
                                    } else {
                                        setManualExists(false);
                                    }
                                })
                                .catch(() => setManualExists(false));
                        }}
                        title="View User Manual"
                        style={{
                            background: "#ffffff",
                            border: "1px solid #e2e8f0",
                            borderRadius: "10px",
                            padding: "10px 14px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            cursor: "pointer",
                            color: "#3b82f6",
                            fontSize: "14px",
                            fontWeight: "600",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#3b82f6"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; }}
                    >
                        <FileText size={18} />
                        User Manual
                    </button>
                </div>
            </div>

            {/* --- User Manual Popup Modal --- */}
            {showManual && (
                <div
                    onClick={() => setShowManual(false)}
                    style={{
                        position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
                        backgroundColor: "rgba(15, 23, 42, 0.6)", display: "flex",
                        alignItems: "center", justifyContent: "center", zIndex: 1100,
                    }}
                >
                    
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: "#ffffff",
                            borderRadius: "12px",
                            width: "90vw",
                            height: "97vh",
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden",
                            boxShadow: "0 20px 25px -5px rgba(0,0,0,0.2)",
                        }}
                    >
                        <div style={{
                            display: "flex", justifyContent: "space-between", alignItems: "center",
                            padding: "14px 20px", borderBottom: "1px solid #e2e8f0",
                        }}>
                            <h4 style={{ margin: 0, color: "#0f172a", fontSize: "16px", fontWeight: "600" }}>
                                User Manual
                            </h4>
                            <button
                                onClick={() => setShowManual(false)}
                                style={{
                                    border: "none", background: "#f1f5f9", borderRadius: "8px",
                                    width: "32px", height: "32px", cursor: "pointer", fontSize: "18px",
                                    color: "#475569", lineHeight: 1,
                                }}
                                title="Close"
                            >
                                ×
                            </button>
                        </div>

                        {manualExists === null && (
                            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: "14px" }}>
                                Checking for user manual...
                            </div>
                        )}

                        {manualExists === false && (
                            <div style={{
                                flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
                                justifyContent: "center", color: "#64748b", gap: "10px", padding: "20px", textAlign: "center"
                            }}>
                                <FileText size={40} color="#cbd5e1" />
                                <div style={{ fontSize: "15px", fontWeight: 600, color: "#334155" }}>
                                    User Manual Not Available
                                </div>
                                <div style={{ fontSize: "13px", color: "#94a3b8", maxWidth: "320px" }}>
                                    The user manual for this module hasn't been uploaded yet. Please check back later or contact your administrator.
                                </div>
                            </div>
                        )}

                        {manualExists === true && manualFile && (() => {
                            const MANUAL_SCALE = 1.25;
                            return (
                                <div style={{ flex: 1, overflow: "hidden", position: "relative" }}>
                                    <iframe
                                        src={manualFile.drive_link.replace("/view", "/preview")}
                                        title={manualFile.file_name || "User Manual"}
                                        allow="autoplay"
                                        style={{
                                            border: "none",
                                            width: `${100 / MANUAL_SCALE}%`,
                                            height: `${100 / MANUAL_SCALE}%`,
                                            transform: `scale(${MANUAL_SCALE})`,
                                            transformOrigin: "top left",
                                        }}
                                    />
                                </div>
                            );
                        })()}
                    </div>
                </div>
            )}

            <div style={styles.filterCard}>
                <div className="row g-3">
                    <div className="col-md-4">
                        <label style={styles.filterLabel}>SO No</label>
                        <Select
                            classNamePrefix="react-select"
                            options={soOptions}
                            value={soFilter}
                            onChange={setSoFilter}
                            placeholder="Search SO No"
                            isClearable
                            isSearchable
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={dropdownOptionStyle}
                        />
                    </div>
                    <div className="col-md-4">
                        <label style={styles.filterLabel}>Product Code</label>
                        <Select
                            classNamePrefix="react-select"
                            options={codeOptions}
                            value={codeFilter}
                            onChange={setCodeFilter}
                            placeholder="Search Product Code"
                            isClearable
                            isSearchable
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={dropdownOptionStyle}
                        />
                    </div>
                    <div className="col-md-4">
                        <label style={styles.filterLabel}>Description</label>
                        <Select
                            classNamePrefix="react-select"
                            options={descOptions}
                            value={descFilter}
                            onChange={setDescFilter}
                            placeholder="Search Description"
                            isClearable
                            isSearchable
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={dropdownOptionStyle}
                        />
                    </div>
                </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
                <div className="pb-tab-toggle">
                    <button
                        type="button"
                        className={blockFilter === 'Blocked' ? 'pb-active-tab' : ''}
                        onClick={() => setBlockFilter('Blocked')}
                    >
                        Blocked {blockedCount > 0 && <span className="pb-badge pb-badge-blocked">{blockedCount}</span>}
                    </button>
                    <button
                        type="button"
                        className={blockFilter === 'Released' ? 'pb-active-tab' : ''}
                        onClick={() => setBlockFilter('Released')}
                    >
                        Released {releasedCount > 0 && <span className="pb-badge pb-badge-released">{releasedCount}</span>}
                    </button>
                </div>
            </div>

            <div style={styles.tableContainer}>
                <table className="table" style={styles.erpTable}>
                    <thead>
                        <tr>
                            <th style={{ ...styles.erpTh, width: '36px', textAlign: 'center' }}>
                                <input
                                    type="checkbox"
                                    checked={allVisibleSelected}
                                    ref={el => { if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected; }}
                                    onChange={toggleSelectAll}
                                />
                            </th>
                            <th style={styles.erpTh}>SO No</th>
                            <th style={styles.erpTh}>Item No</th>
                            <th style={styles.erpTh}>Product Code</th>
                            <th style={styles.erpTh}>Description</th>
                            <th style={{ ...styles.erpTh, textAlign: 'right' }}>Qty</th>
                            <th style={{ ...styles.erpTh, minWidth: '150px' }}>
                                {blockFilter === 'Blocked' ? 'Sales Block Released Date' : 'Planning Block Released Date'}
                            </th>
                            <th style={{ ...styles.erpTh, minWidth: '110px' }}>Planning Block</th>
                            <th style={{ ...styles.erpTh, textAlign: 'center', width: '70px' }}>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td style={styles.erpTd} colSpan={9}>Loading...</td></tr>
                        ) : filteredRows.length === 0 ? (
                            <tr><td style={styles.erpTd} colSpan={9}>No records found</td></tr>
                        ) : (
                            filteredRows.map((row) => {
                                const isReleased = row.planning_block === 'Released';
                                return (
                                    <tr key={row.id} style={isReleased ? styles.releasedRow : undefined}>
                                        <td style={{ ...styles.erpTd, textAlign: 'center' }}>
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.has(row.id)}
                                                disabled={isReleased}
                                                title={isReleased ? "Already Released" : ""}
                                                onChange={() => toggleRow(row.id)}
                                            />
                                        </td>
                                        <td style={styles.erpTd}>
                                            <button
                                                type="button"
                                                style={{ ...styles.soLink, textDecoration: 'underline' }}
                                                title="Open Sales Order"
                                                onClick={() => navigate('/cable/sales/sales-order', { state: { soId: row.so_id, returnTo: location.pathname } })}
                                            >
                                                {row.so_id}
                                            </button>
                                        </td>
                                        <td style={styles.erpTd}>{row.item_no}</td>
                                        <td style={styles.erpTd}>{row.product_code}</td>
                                        <td style={styles.erpTd}>{row.description}</td>
                                        <td style={{ ...styles.erpTd, textAlign: 'right' }}>{row.quantity}</td>
                                        <td style={styles.erpTd}>
                                            {(blockFilter === 'Blocked' ? row.sales_released_date : row.planning_released_date) || '-'}
                                        </td>
                                        <td style={{ ...styles.erpTd, minWidth: '110px' }}>
                                            <select
                                                className="form-control form-control-sm"
                                                style={{ minWidth: '95px' }}
                                                value={row.planning_block}
                                                disabled={savingId === row.id}
                                                onChange={(e) => handleChange(row, e.target.value)}
                                            >
                                                <option value="Blocked">Blocked</option>
                                                <option value="Released">Released</option>
                                            </select>
                                        </td>
                                        <td style={{ ...styles.erpTd, textAlign: 'center' }}>
                                            {row.planning_block === 'Released' ? (
                                                <span style={styles.statusTickWrap} title="Released">
                                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                        <circle cx="8" cy="8" r="8" fill="#22c55e" />
                                                        <path d="M4.5 8.2l2.2 2.2 4.8-4.8" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                                                    </svg>
                                                </span>
                                            ) : (
                                                <span style={styles.statusTickWrap} title="Blocked">
                                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                        <circle cx="8" cy="8" r="8" fill="#ef4444" />
                                                        <path d="M5.2 5.2l5.6 5.6M10.8 5.2l-5.6 5.6" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
                                                    </svg>
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            <ToastContainer position="top-right" autoClose={2500} />
        </Layout>
    );
}

const styles = {
    tableContainer: { background: "#ffffff", borderRadius: 8, padding: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" },
    erpTable: { width: "100%", tableLayout: "auto", borderCollapse: "collapse" },
    erpTh: { background: "#f8fafc", fontSize: 12, fontWeight: 600, padding: "10px 12px", borderBottom: "1px solid #e5e7eb", color: "#1e293b", whiteSpace: "nowrap" },
    erpTd: { fontSize: 13, padding: "10px 12px", borderBottom: "1px solid #f1f5f9", color: "#334155", verticalAlign: "middle" },
    pageTitleBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
    pageTitle: { fontSize: 20, fontWeight: 700, color: '#1e293b' },
    pageSubtitle: { fontSize: 12, color: '#64748b' },
    releaseBtn: {
        background: '#16a34a',
        color: '#fff',
        border: 'none',
        borderRadius: 6,
        padding: '8px 16px',
        fontSize: 13,
        fontWeight: 600,
        cursor: 'pointer',
    },
    releaseBtnDisabled: {
        background: '#94a3b8',
        cursor: 'not-allowed',
    },
    filterCard: { background: '#ffffff', borderRadius: 8, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 16 },
    filterLabel: { fontSize: 12, fontWeight: 600, marginBottom: 6, color: '#334155', display: 'block' },
    releasedRow: { background: '#f8fafc', opacity: 0.65 },
    statusTickWrap: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center' },
    soLink: {
        color: '#2563eb',
        textDecoration: 'none',
        cursor: 'pointer',
        fontWeight: 500,
        background: 'none',
        border: 'none',
        padding: 0,
        margin: 0,
        font: 'inherit',
    },
};

export default PlanningBlock;